import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  User,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';
import { DeletionProtectionModal } from '../../components/UI';
import { useDeletionProtection } from '../../hooks/useDeletionProtection';

interface Customer {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    isActive: true,
  });

  // Deletion protection
  const {
    showDeletionProtectionModal,
    deletionProtectionData,
    checkAndDelete,
    closeDeletionProtectionModal
  } = useDeletionProtection();

  const { settings } = useSettings();

  // Add URL parameter handling for search
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Handle URL search parameter
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchTerm(urlSearch);
    }
  }, [searchParams, setSearchTerm]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        // Clear cache on component mount to ensure fresh data
        apiService.invalidateCustomersCache();
        await fetchCustomers(true);
      } catch (error) {
        console.error('Error fetching customers:', error);
        toast.error('Failed to fetch customers');
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // Listen for global customers refresh from header
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateCustomersCache();
      fetchCustomers(true);
      toast.success('Customers refreshed');
    };
    window.addEventListener('customers:refresh', onRefresh as any);
    return () => window.removeEventListener('customers:refresh', onRefresh as any);
  }, []);

  const fetchCustomers = async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      let response;
      if (forceRefresh) {
        // For force refresh, bypass cache completely
        console.log('Force refreshing customers, bypassing cache');
        response = await apiService.forceRefresh('/customers');
      } else {
        response = await apiService.get('/customers');
      }
      
      console.log('Fetched customers:', response.data.customers?.length || 0, 'customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async () => {
    try {
      const response = await apiService.post('/customers', newCustomer);
      toast.success('Customer added successfully');
      
      // Immediately clear customers cache and force refresh
      apiService.invalidateCustomersCache();
      
      setShowAddModal(false);
      setNewCustomer({
        name: '',
        email: '',
        phone: '',
        address: '',
        isActive: true,
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchCustomers(true);
      }, 100);
    } catch (error) {
      toast.error('Failed to add customer');
    }
  };

  const handleEditCustomer = async () => {
    if (!editingCustomer) return;
    
    try {
      const response = await apiService.put(`/customers/${editingCustomer._id}`, editingCustomer);
      toast.success('Customer updated successfully');
      
      // Immediately clear customers cache and force refresh
      apiService.invalidateCustomersCache();
      
      setShowEditModal(false);
      setEditingCustomer(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchCustomers(true);
      }, 100);
    } catch (error) {
      toast.error('Failed to update customer');
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    const customer = customers.find(c => c._id === customerId);
    if (!customer) return;

    const deleteFunction = async () => {
      try {
        const response = await apiService.delete(`/customers/${customerId}`);
        toast.success('Customer deleted successfully');
        
        // Immediately clear customers cache and force refresh
        apiService.invalidateCustomersCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchCustomers(true);
        }, 100);
      } catch (error) {
        console.error('Error deleting customer:', error);
        toast.error('Failed to delete customer');
      }
    };

    await checkAndDelete('customers', customerId, customer.name, deleteFunction);
  };

  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setShowEditModal(true);
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || customer.isActive === (statusFilter === 'active');
    
    return matchesSearch && matchesStatus;
  });

  const exportCustomers = async () => {
    try {
      await apiService.download('/customers/export/excel', 'customers.xlsx');
      toast.success('Customers exported successfully');
    } catch (error) {
      console.error('Error exporting customers:', error);
      toast.error('Failed to export customers');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-text)] font-[var(--font-family)]">Customers</h1>
          <p className="text-[var(--color-text)] font-[var(--font-family)]">
            Manage your customer information
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              apiService.invalidateCustomersCache();
              fetchCustomers(true);
              toast.success('Cache cleared and data refreshed');
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Customer
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  // Update URL with search parameter
                  if (value) {
                    setSearchParams({ search: value });
                  } else {
                    setSearchParams({});
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportCustomers}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Customers Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <DoubleScrollbar>
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredCustomers.map((customer) => (
                <tr key={customer._id} className="hover:bg-[var(--color-surface-light)]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-[var(--color-primary)] mr-2" />
                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                        {customer.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {customer.email || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {customer.phone || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {customer.address || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-['Times_New_Roman']">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${customer.isActive ? 'bg-[var(--color-success-light)] text-[var(--color-success-dark)]' : 'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)]'}`}>{customer.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{formatDateTime(customer.createdAt, settings)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(customer)}
                        className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-['Times_New_Roman']"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCustomer(customer._id)}
                        className="text-[var(--color-danger)] hover:text-[var(--color-danger-dark)] font-['Times_New_Roman']"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>
        {/* Empty State */}
        {filteredCustomers.length === 0 && (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No customers found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first customer.'}
            </p>
          </div>
        )}
      </div>

      {/* Add Customer Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Customer"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newCustomer.name}
            onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })}
            placeholder="Enter customer name"
            required
          />
          <Input
            label="Email"
            type="email"
            value={newCustomer.email}
            onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
            placeholder="Enter customer email"
          />
          <Input
            label="Phone"
            value={newCustomer.phone}
            onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })}
            placeholder="Enter customer phone"
          />
          <Input
            label="Address"
            value={newCustomer.address}
            onChange={e => setNewCustomer({ ...newCustomer, address: e.target.value })}
            placeholder="Enter customer address"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Status
            </label>
            <select
              value={newCustomer.isActive ? 'active' : 'inactive'}
              onChange={e => setNewCustomer({ ...newCustomer, isActive: e.target.value === 'active' })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddCustomer}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Customer
          </Button>
        </div>
      </Modal>

      {/* Edit Customer Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Customer"
      >
        {editingCustomer && (
          <div className="space-y-4">
            <Input
              label="Name"
              value={editingCustomer.name}
              onChange={e => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
              placeholder="Enter customer name"
              required
            />
            <Input
              label="Email"
              type="email"
              value={editingCustomer.email || ''}
              onChange={e => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
              placeholder="Enter customer email"
            />
            <Input
              label="Phone"
              value={editingCustomer.phone || ''}
              onChange={e => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
              placeholder="Enter customer phone"
            />
            <Input
              label="Address"
              value={editingCustomer.address || ''}
              onChange={e => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
              placeholder="Enter customer address"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Status
              </label>
              <select
                value={editingCustomer.isActive ? 'active' : 'inactive'}
                onChange={e => setEditingCustomer({ ...editingCustomer, isActive: e.target.value === 'active' })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowEditModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditCustomer}
            icon={<Edit className="w-5 h-5" />}
          >
            Update Customer
          </Button>
        </div>
      </Modal>

      {/* Deletion Protection Modal */}
      <DeletionProtectionModal
        isOpen={showDeletionProtectionModal}
        onClose={closeDeletionProtectionModal}
        entityName={deletionProtectionData?.entityName || ''}
        entityType={deletionProtectionData?.entityType || ''}
        dependencies={deletionProtectionData?.dependencies || []}
        onViewDetails={(dependency) => {
          // Navigate to the dependency details
          console.log('View details for:', dependency);
          // You can implement navigation here if needed
        }}
      />
    </div>
  );
};

export default CustomersPage; 